import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../appRedux/store";
import moment, { Moment } from "moment";

import { CustomNotification } from "../../../util/CustomNotification";
import useTablePagination from "../../../hooks/useTablePagination";
import * as reportService from "../../../services/reportsService";
import { ErrorMessageConstants } from "../../../constants/MessageConstants/ErrorMessageConstants";
import { ParkingReportUtil } from "../../../util/ParkingReportUtil";

import type { RangeValue } from "rc-picker/lib/interface";
import type { VisitorData } from "../../../shared/interface/Report/visitorsReport.interface";
import { ExportFile } from "../../../util/ExportFile";
import useValue from "../../../hooks/useValue";
import LocalStorageUtil from "../../../util/LocalStorageUtil";
import { LocalStorageConstants } from "../../../constants/ApplicationConstants/LocalStorageConstants";
import { UserInterface } from "../../../shared/interface/auth/User.interface";

export const useVisitorReport = () => {
  /* CONSTANT */
  const userData = LocalStorageUtil.localStorageGetItem(LocalStorageConstants.USER_DATA) as UserInterface;

  /* Default last 30 selected */
  const days = 30;
  const to = moment();
  const from = moment().subtract(days, "days").startOf("day");

  /* STATE */
  const [visitorData, setVisitorData] = useState<VisitorData[]>([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState<string | null>(from.format("DD-MM-YYYY HH:mm"));
  const [endDate, setEndDate] = useState<string | null>(to.format("DD-MM-YYYY HH:mm"));

  /* CUSTOM HOOKS */
  const { pagination, handleChangePagination, handleChangeTotalSize } = useTablePagination(0);
  const { value: pdfIconLoading, handleDisableValue: handleDisablePdf, handleEnableValue: handleEnablePdf } = useValue(false);

  const { masterFilters, customerList, locationList } = useSelector((state: RootState) => state.common);

  /* HANDLERS */
  const handleDateRangeChange = (values: RangeValue<Moment>, formatString: [string, string]) => {
    if (values && values[0] && values[1]) {
      setStartDate(formatString[0]);
      setEndDate(formatString[1]);
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const fetchVisitorReportData = async () => {
    try {
      if (!masterFilters?.customerId || !masterFilters?.locationId) {
        CustomNotification.showErrorMessage(ErrorMessageConstants.SELECT_CUSTOMER_LOCATION_FILTER);
        return;
      }

      setLoading(true);

      const params = {
        fromDate: startDate,
        toDate: endDate,
        customerId: masterFilters?.customerId,
        locationId: masterFilters?.locationId
      };

      const response = await reportService.getVisitorsReport(params);

      const formattedResponse = ParkingReportUtil.formatParkingHistoryList(response?.data);

      setVisitorData(formattedResponse);

      handleChangeTotalSize(formattedResponse?.length || 0);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDFFile = async () => {
    try {
      handleEnablePdf();

      if (!masterFilters?.customerId || !masterFilters?.locationId) {
        CustomNotification.showErrorMessage(ErrorMessageConstants.SELECT_CUSTOMER_LOCATION_FILTER);
        return;
      }

      const customer = customerList?.find((customer) => customer.id === masterFilters?.customerId);
      const location = locationList?.find((location) => location.id === masterFilters?.locationId);

      const headerInfo: any = {
        header: `${userData?.config?.theme?.HEADER_TEXT || ""}`,
        customerName: `${customer?.customer_name || ""}`,
        locationName: `${location?.location_name || ""}`,
        fromDate: startDate ?? "-",
        toDate: endDate ?? "-"
      };

      const params = {
        fromDate: startDate,
        toDate: endDate,
        customerId: masterFilters?.customerId,
        locationId: masterFilters?.locationId
      };

      const response = await reportService.getVisitorsReport(params);

      const formattedResponse = ParkingReportUtil.formatParkingHistoryList(response?.data);

      const allVisitorData =
        formattedResponse?.map((item, index) => ({
          "Sr No.": index + 1,
          "Visitors Name": item?.full_name || "",
          KeyFob: item?.key_fob_token || "",
          "Vehicle Number": item?.vehicle_number || "",
          "Contact No.": item?.mobile_number || "",
          "In Time": item?.in_time || "",
          "Out Time": item?.out_time || "",
          Duration: item?.booking_retrieval_time_diff || ""
        })) || [];

      ExportFile.pdfFileDownload(allVisitorData, "Visitors Report", headerInfo);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      handleDisablePdf();
    }
  };

  /* EFFECTS */
  useEffect(() => {
    if (masterFilters?.customerId && masterFilters?.locationId) {
      fetchVisitorReportData();
    }
  }, [masterFilters?.customerId, masterFilters?.locationId]);

  return {
    visitorData,
    loading,
    pagination,
    handleChangePagination,
    handleDateRangeChange,
    fetchVisitorReportData,
    setStartDate,
    setEndDate,
    startDate,
    endDate,
    pdfIconLoading,
    handleExportPDFFile
  };
};
